// @flow
import * as React from 'react';

import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Typography,
  // $FlowFixMe
} from '@mui/material';

import InstallationAsBuildForm from 'Components/InstallationAsBuildForm';
// $FlowFixMe
import { pdf } from '@react-pdf/renderer';
import { InstallationAsBuildDocument } from 'Components/DocumentPDF';

const downloadPdfBlob = (blob: Blob, fileName: string) => {
  if (navigator.appVersion.toString().indexOf('.NET') > 0) {
    window.navigator.msSaveOrOpenBlob(blob, fileName);

    return;
  }

  const link = document.createElement('a');
  const url = window.URL.createObjectURL(blob);

  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  window.URL.revokeObjectURL(url);
};

function InstallationAsBuild(): React.Node {
  const [formData, setFormData] = React.useState(null);
  const [loadingMessage, setLoadingMessage] = React.useState(null);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!formData) {
      return undefined;
    }

    let cancelled = false;

    const generatePdf = async () => {
      setLoadingMessage('Generating PDF...');
      setError(null);

      try {
        const blob = await pdf(
          <InstallationAsBuildDocument data={formData} />,
        ).toBlob();

        if (cancelled) {
          return;
        }

        downloadPdfBlob(blob, `${formData.siteId}.pdf`);
      } catch (err) {
        if (!cancelled) {
          setError('Failed to generate PDF. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoadingMessage(null);
        }
      }
    };

    generatePdf();

    return () => {
      cancelled = true;
    };
  }, [formData]);

  if (!formData) {
    return (
      <Box sx={{ flexGrow: 1, position: 'relative' }}>
        <InstallationAsBuildForm
          isProcessing={Boolean(loadingMessage)}
          processingMessage={loadingMessage || 'Preparing report...'}
          onProcessingStart={() => setLoadingMessage('Preparing report...')}
          onProcessingEnd={() => setLoadingMessage(null)}
          onSubmit={async (data) => {
            setFormData(data);
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 64px)',
        gap: 2,
        p: 2,
        position: 'relative',
      }}
    >
      <Backdrop
        open={Boolean(loadingMessage)}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <CircularProgress color="inherit" size={48} />

        <Typography variant="body1" color="inherit">
          {loadingMessage}
        </Typography>
      </Backdrop>

      {error && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 400 }}>
          {error}
        </Alert>
      )}

      {!loadingMessage && (
        <Button
          onClick={() => {
            setFormData(null);
            setError(null);
            setLoadingMessage(null);
          }}
          variant="contained"
        >
          New
        </Button>
      )}
    </Box>
  );
}

export default InstallationAsBuild;
